import React, { useEffect, useState } from 'react';
import {
  FiActivity,
  FiAlertCircle,
  FiArrowDown,
  FiArrowUp,
  FiEye,
  FiFileText,
  FiGlobe,
  FiMail,
  FiMonitor,
  FiRefreshCw,
  FiSmartphone,
  FiTablet,
  FiTrendingUp,
  FiUsers,
  FiZap
} from 'react-icons/fi';
import {
  Area,
  AreaChart,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import apiService from '../services/api';

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    
    // Fetch dashboard data on mount only
    fetchDashboardData();
    
    return () => {
      clearInterval(timer);
    };
  }, []);

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      }
      setError(null);
      const response = await apiService.getDashboardAnalytics();
      setDashboardData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    fetchDashboardData(true);
  };

  // Default fallback data if API fails
  const fallbackStats = [
    {
      title: 'Website Visitors',
      value: '0',
      change: '0%',
      trend: 'up',
      icon: FiEye,
      gradient: 'from-cyan-400 via-blue-500 to-purple-600',
      bgGradient: 'from-cyan-500/20 via-blue-500/20 to-purple-600/20',
      borderColor: 'border-cyan-400/30'
    },
    {
      title: 'Newsletter Subscribers',
      value: '0',
      change: '0%',
      trend: 'up',
      icon: FiMail,
      gradient: 'from-emerald-400 via-teal-500 to-green-600',
      bgGradient: 'from-emerald-500/20 via-teal-500/20 to-green-600/20',
      borderColor: 'border-emerald-400/30'
    },
    {
      title: 'Resume Uploads',
      value: '0',
      change: '0%',
      trend: 'up',
      icon: FiFileText,
      gradient: 'from-violet-400 via-purple-500 to-pink-600',
      bgGradient: 'from-violet-500/20 via-purple-500/20 to-pink-600/20',
      borderColor: 'border-violet-400/30'
    },
    {
      title: 'Active Subscribers',
      value: '0',
      change: '0%',
      trend: 'up',
      icon: FiUsers,
      gradient: 'from-amber-400 via-orange-500 to-red-600',
      bgGradient: 'from-amber-500/20 via-orange-500/20 to-red-600/20',
      borderColor: 'border-amber-400/30'
    }
  ];

  // Process real data into stats format
  const processStats = (data) => {
    if (!data || !data.stats) return fallbackStats;

    const { stats } = data;
    
    return [
      {
        title: 'Total Contacts',
        value: stats.totalContacts?.value?.toLocaleString() || '0',
        change: `${stats.totalContacts?.change || 0}%`,
        trend: stats.totalContacts?.trend || 'up',
        icon: FiEye,
        gradient: 'from-cyan-400 via-blue-500 to-purple-600',
        bgGradient: 'from-cyan-500/20 via-blue-500/20 to-purple-600/20',
        borderColor: 'border-cyan-400/30',
        weekly: stats.totalContacts?.weekly || 0,
        monthly: stats.totalContacts?.monthly || 0
      },
      {
        title: 'Newsletter Subscribers',
        value: stats.totalSubscribers?.value?.toLocaleString() || '0',
        change: `${stats.totalSubscribers?.change || 0}%`,
        trend: stats.totalSubscribers?.trend || 'up',
        icon: FiMail,
        gradient: 'from-emerald-400 via-teal-500 to-green-600',
        bgGradient: 'from-emerald-500/20 via-teal-500/20 to-green-600/20',
        borderColor: 'border-emerald-400/30',
        weekly: stats.totalSubscribers?.weekly || 0,
        monthly: stats.totalSubscribers?.monthly || 0,
        active: stats.totalSubscribers?.active || 0
      },
      {
        title: 'Content Published',
        value: stats.totalContent?.published?.toLocaleString() || '0',
        change: '+0%',
        trend: 'up',
        icon: FiFileText,
        gradient: 'from-violet-400 via-purple-500 to-pink-600',
        bgGradient: 'from-violet-500/20 via-purple-500/20 to-pink-600/20',
        borderColor: 'border-violet-400/30',
        total: stats.totalContent?.value || 0,
        drafts: stats.totalContent?.drafts || 0
      },
      {
        title: 'Active Subscribers',
        value: stats.totalSubscribers?.active?.toLocaleString() || '0',
        change: `${stats.totalSubscribers?.change || 0}%`,
        trend: stats.totalSubscribers?.trend || 'up',
        icon: FiUsers,
        gradient: 'from-amber-400 via-orange-500 to-red-600',
        bgGradient: 'from-amber-500/20 via-orange-500/20 to-red-600/20',
        borderColor: 'border-amber-400/30',
        total: stats.totalSubscribers?.value || 0
      }
    ];
  };

  const stats = processStats(dashboardData);
  const weeklyData = dashboardData?.charts?.weekly || [];
  const recentContacts = dashboardData?.activity?.recentContacts || [];
  const recentSubscribers = dashboardData?.activity?.recentSubscribers || [];
  const popularServices = dashboardData?.charts?.services || [];

  // Default device data (could be enhanced with real analytics)
  const deviceData = [
    { name: 'Desktop', value: 45, color: '#06b6d4', icon: FiMonitor },
    { name: 'Mobile', value: 38, color: '#10b981', icon: FiSmartphone },
    { name: 'Tablet', value: 17, color: '#f59e0b', icon: FiTablet }
  ];

  const performanceData = [
    { name: 'Performance', value: 92, color: '#06b6d4' },
    { name: 'SEO', value: 88, color: '#10b981' },
    { name: 'Accessibility', value: 95, color: '#8b5cf6' },
    { name: 'Best Practices', value: 90, color: '#f59e0b' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <span className="text-white text-lg">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black relative">
      {/* Simplified Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-10"></div>
      </div>

      <div className="relative z-10 p-6 space-y-8 max-w-7xl mx-auto">
        {/* Header with Manual Refresh */}
        <div className="relative">
          <div className="bg-gradient-to-r from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent font-display mb-2">
                  Innofusion Admin Dashboard
                </h1>
                <p className="text-slate-300 text-lg">
                  Monitor your business metrics and performance
                </p>
                {lastUpdated && (
                  <p className="text-slate-400 text-sm mt-2">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-white mb-1">
                  {currentTime.toLocaleTimeString()}
                </div>
                <div className="text-slate-400 text-sm mb-3">
                  {currentTime.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
                <button
                  onClick={handleManualRefresh}
                  disabled={refreshing}
                  className={`px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/30 rounded-lg text-cyan-400 text-sm transition-all duration-300 flex items-center gap-2 ${
                    refreshing ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                  }`}
                >
                  <FiRefreshCw className={`${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </button>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-500/20 border border-red-400/30 rounded-lg flex items-center text-red-400">
                <FiAlertCircle className="mr-2" />
                <span>Error loading data: {error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid - Simplified animations */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={stat.title} className="relative group">
                <div className={`bg-gradient-to-br ${stat.bgGradient} backdrop-blur-xl rounded-2xl p-6 border ${stat.borderColor} shadow-2xl hover:shadow-3xl transition-all duration-300 h-full hover:-translate-y-1`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.gradient} shadow-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className={`flex items-center text-sm font-medium px-3 py-1 rounded-full ${
                      stat.trend === 'up' 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-400/30' 
                        : 'bg-red-500/20 text-red-400 border border-red-400/30'
                    }`}>
                      {stat.trend === 'up' ? (
                        <FiArrowUp className="w-3 h-3 mr-1" />
                      ) : (
                        <FiArrowDown className="w-3 h-3 mr-1" />
                      )}
                      {stat.change}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-slate-300 text-sm font-medium">{stat.title}</h3>
                    <div className="text-3xl font-bold text-white">
                      {stat.value}
                    </div>
                    {stat.weekly !== undefined && (
                      <div className="text-xs text-slate-400">
                        <span className="text-cyan-400">+{stat.weekly}</span> this week
                        {stat.active !== undefined && (
                          <span className="ml-2 text-emerald-400">({stat.active} active)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Weekly Analytics */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Weekly Overview</h3>
              <FiTrendingUp className="w-5 h-5 text-cyan-400" />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={weeklyData}>
                <defs>
                  <linearGradient id="visitorsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="subscribersGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="day" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'rgba(30, 41, 59, 0.9)',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)'
                  }}
                  labelStyle={{ color: '#f1f5f9' }}
                />
                <Area
                  type="monotone"
                  dataKey="visitors"
                  stroke="#06b6d4"
                  strokeWidth={2}
                  fill="url(#visitorsGradient)"
                  name="Visitors"
                />
                <Area
                  type="monotone"
                  dataKey="subscribers"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#subscribersGradient)"
                  name="Subscribers"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Device Breakdown */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Device Analytics</h3>
              <FiMonitor className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={deviceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      borderRadius: '12px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {deviceData.map((device) => {
                const Icon = device.icon;
                return (
                  <div key={device.name} className="text-center">
                    <div 
                      className="p-3 rounded-xl mx-auto mb-2 w-fit"
                      style={{ backgroundColor: `${device.color}20`, border: `1px solid ${device.color}30` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: device.color }} />
                    </div>
                    <div className="text-sm text-slate-300">{device.name}</div>
                    <div className="text-lg font-bold text-white">{device.value}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent Activity and Popular Services */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Contacts */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Recent Contacts</h3>
              <FiActivity className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="space-y-4">
              {recentContacts.length > 0 ? recentContacts.map((contact) => (
                <div key={contact._id} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl border border-slate-600/30 hover:bg-slate-700/50 transition-all duration-300">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                      {contact.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{contact.name || 'Unknown'}</p>
                      <p className="text-slate-400 text-sm">{contact.email}</p>
                      <p className="text-slate-500 text-xs">{contact.service || 'General Inquiry'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      contact.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                      contact.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-400' :
                      contact.status === 'qualified' ? 'bg-green-500/20 text-green-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {contact.status || 'new'}
                    </div>
                    <p className="text-slate-400 text-xs mt-1">
                      {new Date(contact.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-slate-400">
                  <FiUsers className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No recent contacts</p>
                </div>
              )}
            </div>
          </div>

          {/* Popular Services */}
          <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Popular Services</h3>
              <FiZap className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="space-y-4">
              {popularServices.length > 0 ? popularServices.map((service, index) => (
                <div key={service._id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm font-bold">{index + 1}</span>
                    </div>
                    <span className="text-white font-medium">{service._id}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-slate-400">{service.count} requests</span>
                    <div className="w-16 h-2 bg-slate-700 rounded-full">
                      <div
                        style={{ width: `${(service.count / Math.max(...popularServices.map(s => s.count))) * 100}%` }}
                        className="h-full bg-gradient-to-r from-purple-400 to-pink-500 rounded-full transition-all duration-1000"
                      />
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-slate-400">
                  <FiZap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No service data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Website Performance</h3>
            <FiGlobe className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {performanceData.map((metric) => (
              <div key={metric.name} className="text-center">
                <ResponsiveContainer width="100%" height={120}>
                  <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" data={[metric]}>
                    <RadialBar
                      dataKey="value"
                      cornerRadius={10}
                      fill={metric.color}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="mt-2">
                  <div className="text-2xl font-bold text-white">{metric.value}%</div>
                  <div className="text-sm text-slate-400">{metric.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
